import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { salvarRascunho, carregarRascunho } from './db';
import type { DraftData } from './types';
import { getAtividades } from './activities';
import { getIdentificacao } from './partners';
import { getObjetoProjeto } from './object';
import {
  atualizarChecklistCaracterizacao,
  atualizarChecklistJustificativa,
  atualizarChecklistDescAtividades,
  atualizarChecklistDireitosPI
} from './progress';
import { atualizarChecklistMeios } from './progress';
import { syncProgressoMeios } from './meios';
import { setLastEditNow } from './lastedit';

type EditorMap = Record<string, Quill>;

const EDITOR_IDS = [
  'objetivosGerais',
  'objetivosEspecificos',
  'justificativa',
  'responsabilidadesUFC',
  'responsabilidadesParceiro',
  'atividadesConjuntas',
  'direitosPI',
  'recursosFinanceiros',
  'infraestrutura'
] as const;

let editors: EditorMap = {};

function initQuill(selector: string): Quill {
  return new Quill(`#${selector}`, {
    theme: 'snow',
    modules: {
      toolbar: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'blockquote', 'code-block'],
        [{ align: [] }],
        ['clean']
      ]
    }
  });
}

export function initEditores(): void {
  for (const id of EDITOR_IDS) {
    const container = document.getElementById(id);
    if (!container) continue;
    const quill = initQuill(id);
    editors[id] = quill;

    quill.on('text-change', (_delta, _old, source) => {
      void salvarTudo();
      if (id === 'objetivosGerais' || id === 'objetivosEspecificos') {
        atualizarChecklistCaracterizacao(getObjetivosGeraisHTML(), getObjetivosEspecificosHTML());
      }
      if (id === 'justificativa') {
        atualizarChecklistJustificativa(getJustificativaHTML());
      }
      if (id === 'responsabilidadesUFC' || id === 'responsabilidadesParceiro' || id === 'atividadesConjuntas') {
        atualizarChecklistDescAtividades(
          getResponsabilidadesUFCHTML(),
          getResponsabilidadesParceiroHTML(),
          getAtividadesConjuntasHTML()
        );
      }
      if (id === 'recursosFinanceiros' || id === 'infraestrutura') {
        void syncProgressoMeios();
      }

      if (id === 'direitosPI') {
        atualizarChecklistDireitosPI(getDireitosPIHTML());
      }
      if (source === 'user') {
        setLastEditNow();
      }
    });
  }

  void preencherObjetivos(awaitRascunho());
}

async function awaitRascunho(): Promise<DraftData | null> {
  try {
    return await carregarRascunho();
  } catch {
    return null;
  }
}

/** Converte delta -> HTML simplificado */
function deltaToHTML(quill: Quill): string {
  const html = quill.root.innerHTML;
  return html;
}

/** Cola HTML no editor preservando formatação básica */
function pasteHTML(quill: Quill, html?: string) {
  if (!html) return;
  quill.clipboard.dangerouslyPasteHTML(html);
}

export function getObjetivosGeraisHTML(): string {
  return editors['objetivosGerais'] ? deltaToHTML(editors['objetivosGerais']) : '';
}
export function getObjetivosEspecificosHTML(): string {
  return editors['objetivosEspecificos'] ? deltaToHTML(editors['objetivosEspecificos']) : '';
}
export function getJustificativaHTML(): string {
  return editors['justificativa'] ? deltaToHTML(editors['justificativa']) : '';
}
export function getResponsabilidadesUFCHTML(): string {
  return editors['responsabilidadesUFC'] ? deltaToHTML(editors['responsabilidadesUFC']) : '';
}
export function getResponsabilidadesParceiroHTML(): string {
  return editors['responsabilidadesParceiro'] ? deltaToHTML(editors['responsabilidadesParceiro']) : '';
}
export function getAtividadesConjuntasHTML(): string {
  return editors['atividadesConjuntas'] ? deltaToHTML(editors['atividadesConjuntas']) : '';
}
export function getDireitosPIHTML(): string {
  return editors['direitosPI'] ? deltaToHTML(editors['direitosPI']) : '';
}
export function getRecursosFinanceirosHTML(): string {
  return editors['recursosFinanceiros']?.root?.innerHTML ?? '';
}
export function getInfraestruturaHTML(): string {
  return editors['infraestrutura']?.root?.innerHTML ?? '';
}

export async function preencherObjetivos(dataPromise?: Promise<DraftData | null>) {
  const data = dataPromise ? await dataPromise : await carregarRascunho();
  if (!data) return;

  pasteHTML(editors['objetivosGerais'], data.objetivosGerais);
  pasteHTML(editors['objetivosEspecificos'], data.objetivosEspecificos);
  pasteHTML(editors['justificativa'], data.justificativa);
  pasteHTML(editors['responsabilidadesUFC'], data.responsabilidadesUFC);
  pasteHTML(editors['responsabilidadesParceiro'], data.responsabilidadesParceiro);
  pasteHTML(editors['atividadesConjuntas'], data.atividadesConjuntas);
  pasteHTML(editors['direitosPI'], data.direitosPI);
  pasteHTML(editors['recursosFinanceiros'], data.recursosFinanceiros);
  pasteHTML(editors['infraestrutura'], data.infraestrutura);
}

async function salvarTudo() {
  const atuais = (await carregarRascunho()) || ({} as DraftData);
  const identificacao = getIdentificacao();
  const objeto = getObjetoProjeto();
  const atividades = getAtividades();

  const novo: DraftData = {
    ...atuais,
    ...identificacao,
    ...objeto,
    atividades,
    objetivosGerais: getObjetivosGeraisHTML(),
    objetivosEspecificos: getObjetivosEspecificosHTML(),
    justificativa: getJustificativaHTML(),
    responsabilidadesUFC: getResponsabilidadesUFCHTML(),
    responsabilidadesParceiro: getResponsabilidadesParceiroHTML(),
    atividadesConjuntas: getAtividadesConjuntasHTML(),
    direitosPI: getDireitosPIHTML(),
    recursosFinanceiros: getRecursosFinanceirosHTML(),
    infraestrutura: getInfraestruturaHTML()
  };

  await salvarRascunho(novo);

}
